/**
 * ModeToggleCompact Component
 *
 * Compact version of mode toggle for the redesigned Live Activity Dashboard.
 * Takes less vertical space while maintaining all functionality.
 *
 * Features:
 * - Visual toggle between Live and Heatmap modes
 * - Time window selector (1h, 4h, 8h, Today) for Heatmap mode
 * - Mode persistence to localStorage
 * - Compact, professional design
 *
 * @author BizClick WMS
 * @version 2.0 - Compact redesign
 */

import React, { useEffect, useState } from 'react';
import { Radio, BarChart3, Clock } from 'lucide-react';
import { TimeWindow } from './ActivityCacheManager';
import * as localStorageHelper from '../../../../utils/localStorageHelper';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface ModeToggleCompactProps {
  /** Current mode */
  mode: 'live' | 'heatmap';

  /** Callback when mode changes */
  onModeChange: (mode: 'live' | 'heatmap') => void;

  /** Current time window (for heatmap mode) */
  timeWindow: TimeWindow;

  /** Callback when time window changes */
  onTimeWindowChange: (window: TimeWindow) => void;

  /** Optional: Disable mode switching */
  disabled?: boolean;
}

/**
 * localStorage keys for persistence
 */
const MODE_STORAGE_KEY = 'bizclick_dashboard_mode';
const TIME_WINDOW_STORAGE_KEY = 'bizclick_dashboard_timewindow';

export const ModeToggleCompact: React.FC<ModeToggleCompactProps> = ({
  mode,
  onModeChange,
  timeWindow,
  onTimeWindowChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  /**
   * Time window labels (localized, compact)
   */
  const TIME_WINDOW_LABELS: Record<TimeWindow, string> = {
    [TimeWindow.Last1Hour]: t('manager.modules.rackBuilderLive.timeWindowCompact.hour1', {}, '1t'),
    [TimeWindow.Last4Hours]: t('manager.modules.rackBuilderLive.timeWindowCompact.hours4', {}, '4t'),
    [TimeWindow.Last8Hours]: t('manager.modules.rackBuilderLive.timeWindowCompact.hours8', {}, '8t'),
    [TimeWindow.Today]: t('manager.modules.rackBuilderLive.timeWindow.today', {}, 'I dag')
  };

  // Load persisted mode and time window on mount
  useEffect(() => {
    setMounted(true);

    // Restore mode from localStorage
    const savedMode = localStorageHelper.getItem<'live' | 'heatmap'>(MODE_STORAGE_KEY);
    if (savedMode && (savedMode === 'live' || savedMode === 'heatmap')) {
      onModeChange(savedMode);
    }

    // Restore time window from localStorage
    const savedTimeWindow = localStorageHelper.getItem<TimeWindow>(TIME_WINDOW_STORAGE_KEY);
    if (savedTimeWindow && Object.values(TimeWindow).includes(savedTimeWindow)) {
      onTimeWindowChange(savedTimeWindow);
    }
  }, []);

  /**
   * Handle mode change with persistence
   */
  const handleModeChange = (newMode: 'live' | 'heatmap') => {
    if (disabled) return;

    onModeChange(newMode);
    localStorageHelper.setItem(MODE_STORAGE_KEY, newMode);
  };

  /**
   * Handle time window change with persistence
   */
  const handleTimeWindowChange = (newWindow: TimeWindow) => {
    onTimeWindowChange(newWindow);
    localStorageHelper.setItem(TIME_WINDOW_STORAGE_KEY, newWindow);
  };

  if (!mounted) {
    return null; // Prevent SSR hydration issues
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 bg-gradient-to-r from-gray-50 to-transparent border-b border-gray-100">
        <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide">
          {t('manager.modules.rackBuilderLive.modeToggleCompact.viewMode', {}, 'Visningsmodus')}
        </h4>
      </div>

      {/* Mode Toggle Buttons */}
      <div className="p-3">
        <div className="flex gap-2">
          {/* Live Mode Button */}
          <button
            onClick={() => handleModeChange('live')}
            disabled={disabled}
            className={`
              flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold
              transition-all duration-200
              ${mode === 'live'
                ? 'bg-orange-500 text-white shadow-md shadow-orange-500/25'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <Radio className={`w-4 h-4 ${mode === 'live' ? 'animate-pulse' : ''}`} />
            <span>Live</span>
          </button>

          {/* Heatmap Mode Button */}
          <button
            onClick={() => handleModeChange('heatmap')}
            disabled={disabled}
            className={`
              flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold
              transition-all duration-200
              ${mode === 'heatmap'
                ? 'bg-teal-600 text-white shadow-md shadow-teal-600/25'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <BarChart3 className="w-4 h-4" />
            <span>{t('manager.modules.rackBuilderLive.modeToggle.heatmap', {}, 'Heatmap')}</span>
          </button>
        </div>

        {/* Time Window Selector (Heatmap only) */}
        {mode === 'heatmap' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-semibold text-gray-600">{t('manager.modules.rackBuilderLive.modeToggleCompact.timeWindow', {}, 'Tidsvindu')}</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.values(TimeWindow).map((window) => (
                <button
                  key={window}
                  onClick={() => handleTimeWindowChange(window)}
                  className={`
                    py-1.5 rounded-md text-xs font-semibold
                    transition-all duration-200
                    ${timeWindow === window
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  {TIME_WINDOW_LABELS[window]}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mode Description Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          {mode === 'live' ? (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></span>
              {t('manager.modules.rackBuilderLive.modeToggleCompact.liveDescription', {}, 'Sanntidsaktivitet med animasjoner')}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
              {t('manager.modules.rackBuilderLive.modeToggleCompact.heatmapDescription', {}, 'Aktivitetsintensitet over tid')}
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default ModeToggleCompact;
